import { ShopTheme, toWireTheme, validateShopTheme } from '../theme/shopTheme';
import { API_URL, THEMES } from '../utils/helpers';

export const PORTAL_THEME_CACHE_PREFIX = 'portalTheme:';
// #189 (ra#499 S3): the shop's colours and fonts, applied in the first render
export const PORTAL_CUSTOM_THEME_CACHE_PREFIX = 'portalCustomTheme:';
// an applied layer always sits on this base (ra reports it while one applies)
const LAYER_BASE = 'version2';

const knownTheme = (value: unknown): string | undefined =>
  THEMES.find(theme => theme.name === value)?.name;

export const shopDomainOf = (search: string): string | null =>
  new URLSearchParams(search).get('shop')?.trim().toLowerCase() || null;

export const cachedPortalTheme = (
  shopDomain: string | null
): string | undefined => {
  if (!shopDomain?.trim()) return undefined;
  try {
    return knownTheme(
      localStorage.getItem(PORTAL_THEME_CACHE_PREFIX + shopDomain)
    );
  } catch {
    return undefined;
  }
};

export const cachedCustomTheme = (
  shopDomain: string | null
): ShopTheme | null => {
  if (!shopDomain?.trim()) return null;
  try {
    const raw = localStorage.getItem(
      PORTAL_CUSTOM_THEME_CACHE_PREFIX + shopDomain
    );
    return raw === null ? null : validateShopTheme(JSON.parse(raw));
  } catch {
    return null;
  }
};

export type PortalThemeResult =
  | { kind: 'set'; theme: string; customTheme?: ShopTheme }
  | { kind: 'unset' }
  | { kind: 'failed' };

export const fetchPortalTheme = async (
  shopDomain: string,
  signal?: AbortSignal
): Promise<PortalThemeResult> => {
  let result: PortalThemeResult;
  try {
    const response = await fetch(API_URL + '/settings/portal', {
      headers: { Shop: shopDomain },
      signal,
    });
    if (!response.ok) return { kind: 'failed' };
    const body = await response.json();
    if (
      body === null ||
      typeof body !== 'object' ||
      Object.getPrototypeOf(body) !== Object.prototype ||
      !Object.prototype.hasOwnProperty.call(body, 'portal_theme')
    ) {
      return { kind: 'failed' };
    }
    if (body.portal_theme === null) {
      result = { kind: 'unset' };
    } else {
      const theme = knownTheme(body.portal_theme);
      if (theme === undefined) return { kind: 'failed' };
      // a malformed layer is ignored; the preset still stands
      const customTheme =
        theme === LAYER_BASE ? validateShopTheme(body.custom_theme) : null;
      result =
        customTheme === null
          ? { kind: 'set', theme }
          : { kind: 'set', theme, customTheme };
    }
  } catch {
    // A failed request must not invalidate the last successful shop setting.
    return { kind: 'failed' };
  }

  if (signal?.aborted) return { kind: 'failed' };

  try {
    const key = PORTAL_THEME_CACHE_PREFIX + shopDomain;
    if (result.kind === 'set') localStorage.setItem(key, result.theme);
    else localStorage.removeItem(key);
    const customKey = PORTAL_CUSTOM_THEME_CACHE_PREFIX + shopDomain;
    if (result.kind === 'set' && result.customTheme !== undefined) {
      localStorage.setItem(
        customKey,
        JSON.stringify(toWireTheme(result.customTheme))
      );
    } else {
      localStorage.removeItem(customKey);
    }
  } catch {
    // Storage may be unavailable; the fetched setting still applies.
  }
  return result;
};
